import { useMemo, useState } from 'react';
import { User, X } from 'lucide-react';
import { useLingui } from '@lingui/react';
import { CommandItem } from '@/components/ui/command';
import { ServerSearchCombobox } from '@/components/ui/server-search-combobox';
import {
  formatEmployeeName,
  isSelectableEmployee,
  matchesEmployeeName,
  useEmployeePicker,
} from '@/hooks/useEmployees';
import type { EmployeePickerEntry } from '@/types';

export interface EmployeeSelectorMessages {
  choose?: string;
  search?: string;
  empty?: string;
  loading?: string;
  loadFailed?: string;
  clear?: string;
}

export interface EmployeeSelectorProps {
  value?: string;
  onChange: (id: string | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /** Put on the combobox trigger so a `<label htmlFor>` resolves to it. */
  id?: string;
  /** Forwarded to the trigger as `aria-describedby` (error / description text). */
  ariaDescribedBy?: string;
  /** Forwarded to the trigger as `aria-invalid`. */
  ariaInvalid?: boolean;
  /** Forwarded to the trigger as `aria-required`. */
  ariaRequired?: boolean;
  /**
   * Hidden from the rendered list. The direct-head field passes the employee
   * being edited: the backend rejects a self-reference anyway, so offering it
   * would be offering a guaranteed error.
   */
  excludeEmployeeId?: string;
  /**
   * Offer only employees with a login (`clerkUserId`), for pickers whose target
   * must sign in to act (a workflow hand-over). The current value stays listed.
   */
  requireLogin?: boolean;
  /**
   * Overrides for the six user-visible strings. Callers with their own message
   * ids (EmployeeSearchSelect) pass them here so their catalogue entries stay
   * put; everyone else gets the shared `employeeSelector.*` ids.
   */
  messages?: EmployeeSelectorMessages;
}

/** Rows shown per search, like the former server-side search page. */
const MAX_ITEMS = 50;

/**
 * The single employee picker (issue #1217).
 *
 * Built on `ServerSearchCombobox` following the ContactSelector / AddressSelector
 * precedent: the primitive owns debounce, reset-on-close and the
 * selected-label-outside-results problem; this component owns fetching.
 *
 * The source is the slim any-module picker (`GET /employees/picker`, #1688): the
 * whole staff in one cached request, searched in the browser. It offers ACTIVE
 * and UPCOMING employees (`isSelectableEmployee`) plus the current value. Every page that
 * hosts the selector (settling-in, groups, employees) therefore works for any
 * role, and a selection outside the shown rows still renders its label because
 * it is resolved from the same list.
 */
export function EmployeeSelector({
  value,
  onChange,
  disabled,
  placeholder,
  ariaLabel,
  id,
  ariaDescribedBy,
  ariaInvalid,
  ariaRequired,
  excludeEmployeeId,
  requireLogin,
  messages,
}: EmployeeSelectorProps) {
  const { i18n } = useLingui();
  const [search, setSearch] = useState('');
  const pickerQuery = useEmployeePicker();

  const items = useMemo(
    () =>
      (pickerQuery.data ?? [])
        .filter(
          (employee) =>
            employee.id !== excludeEmployeeId &&
            (employee.id === value ||
              (isSelectableEmployee(employee) && (!requireLogin || !!employee.clerkUserId))) &&
            matchesEmployeeName(employee, search),
        )
        .slice(0, MAX_ITEMS),
    [pickerQuery.data, excludeEmployeeId, requireLogin, search, value],
  );

  // Guarded on `value`: with no selection there is nothing to resolve.
  const selectedItem = value
    ? (pickerQuery.data ?? []).find((employee) => employee.id === value)
    : undefined;

  const label =
    ariaLabel ?? i18n._({ id: 'employeeSelector.choose', message: 'Choose an employee...' });

  const chooseText =
    messages?.choose ?? i18n._({ id: 'employeeSelector.choose', message: 'Choose an employee...' });
  const clearText =
    messages?.clear ?? i18n._({ id: 'employeeSelector.clear', message: 'Clear selection' });
  const searchText =
    placeholder ??
    messages?.search ??
    i18n._({ id: 'employeeSelector.search', message: 'Search by name...' });
  const emptyText =
    messages?.empty ?? i18n._({ id: 'employeeSelector.empty', message: 'No employees found' });
  const loadingText =
    messages?.loading ?? i18n._({ id: 'employeeSelector.loading', message: 'Loading employees...' });

  const showClear =
    !!value && !pickerQuery.isLoading && !pickerQuery.isFetching && !pickerQuery.isError;

  return (
    <ServerSearchCombobox<EmployeePickerEntry>
      value={value || undefined}
      onChange={(next) => onChange(next)}
      items={items}
      selectedItem={selectedItem}
      getItemId={(employee) => employee.id}
      onDebouncedSearchChange={setSearch}
      isLoading={pickerQuery.isLoading}
      isFetching={pickerQuery.isFetching}
      isError={pickerQuery.isError}
      disabled={disabled}
      triggerAriaLabel={label}
      triggerId={id}
      triggerAriaDescribedBy={ariaDescribedBy}
      triggerAriaInvalid={ariaInvalid}
      triggerAriaRequired={ariaRequired}
      searchPlaceholder={searchText}
      emptyMessage={emptyText}
      loadingMessage={loadingText}
      errorMessage={
        messages?.loadFailed ??
        i18n._({
          id: 'employeeSelector.loadFailed',
          message: 'Employees could not be loaded. Close and reopen to retry.',
        })
      }
      renderTrigger={(selected) =>
        selected ? (
          <span className="flex items-center gap-2 truncate">
            <User className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{formatEmployeeName(selected)}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">{chooseText}</span>
        )
      }
      renderItem={(employee) => (
        <span className="flex items-center gap-2 truncate">
          <User className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate">{formatEmployeeName(employee)}</span>
        </span>
      )}
      // The primitive's re-select-to-clear toggle is not enough on its own:
      // under server-side search the selected row is frequently absent from the
      // current page, so there is often no row to re-click.
      //
      // Rendered ONLY when there is something to clear and the query has
      // settled. Any CommandItem makes cmdk's match count non-zero, which
      // suppresses CommandEmpty — an unconditional footer would hide the
      // primitive's loading and error messages, and "the search never
      // completed" must never be silently swallowed.
      renderFooter={showClear ? (close) => (
        <CommandItem
          value="__employee-selector-clear__"
          onSelect={() => {
            onChange(undefined);
            close();
          }}
        >
          <span className="flex items-center gap-2 text-muted-foreground">
            <X className="h-4 w-4 shrink-0" aria-hidden="true" />
            {clearText}
          </span>
        </CommandItem>
      ) : undefined}
    />
  );
}

export default EmployeeSelector;
